import moment from 'moment';

export function RestaurantHoursService(
    $q,
    ORDER_TYPES,
    availabilitiesFactory,
    restaurantInfoFactory
) {
    'ngInject';
    var data = {
        initialMoment: null
    };

    var _data = {
        timeZone: null,
        prepTimes: null,
        offsetsMap: {
            [ORDER_TYPES.DELIVERY]: {},
            [ORDER_TYPES.TAKE_OUT]: {}
        }
    };

    var service = {
        getMinMoment,
        getOffsetTime,
        getThrottlingTime,
        initialize,
        isRestaurantOpen,
        setPrepTime,
        setInitialDateTime,
        timeUntilClose,
        data,
        isSameDay,
        getRestaurantAvailability,
        firstValidServicePeriod,
        _data
    };

    return service;

    ///---------------

    function initialize() {
        return restaurantInfoFactory.getInfo(['general', 'onlineOrder', 'prepTimes'])
            .then(_initializeHelper);
    }

    function _initializeHelper(info) {
        _data.closeoutHour = +info.general.closeoutHour;
        _data.timeZone = info.general.timeZone;
        _data.enableThrottling = Boolean(info.prepTimes.onlineOrderThrottlingEnabled);
        _data.enableScheduling = Boolean(info.onlineOrder.enableScheduling);
        _data.scheduledOrderMaxDays = info.onlineOrder.scheduledOrderMaxDays || 1;


        Object.assign(_data.offsetsMap[ORDER_TYPES.DELIVERY], {
            throttleTime: info.prepTimes.deliveryThrottlingTime,
            originalPrepTime: info.prepTimes.deliveryPrepTime
        });

        Object.assign(_data.offsetsMap[ORDER_TYPES.TAKE_OUT], {
            throttleTime: info.prepTimes.takeoutThrottlingTime,
            originalPrepTime: info.prepTimes.takeoutPrepTime
        });
    }

    function setPrepTime(prepTime, diningOption) {
        if (_data.offsetsMap[diningOption]) {
            const oldPrep = angular.copy(_data.offsetsMap[diningOption].prepTime);

            restaurantInfoFactory.getInfo(['prepTimes']).then(info => {
                if (diningOption === ORDER_TYPES.DELIVERY) {
                    info.prepTimes.deliveryPrepTime = prepTime;
                } else {
                    info.prepTimes.takeoutPrepTime = prepTime;
                }

                restaurantInfoFactory.setInfo({
                    prepTimes: info.prepTimes
                });
            });

            Object.assign(_data.offsetsMap[diningOption], { prepTime });
            return _prepTimeUpdated(oldPrep, prepTime);
        }
    }

    function _prepTimeUpdated(oldPrep, newPrep) {
        if(!oldPrep) return false;
        if(!newPrep) return false;
        return oldPrep !== newPrep;
    }

    function getThrottlingTime(diningOption) {
        if(_data.enableThrottling && _data.offsetsMap[diningOption]) {
            return _data.offsetsMap[diningOption].throttleTime;
        }
        return 0;
    }

    function getOffsetTime(diningOption) {
        let offsetTime = 0;
        if(!_data.offsetsMap[diningOption]) return offsetTime;
        if(_data.offsetsMap[diningOption]) {
            offsetTime += _data.offsetsMap[diningOption].prepTime;
        }
        if(_data.enableThrottling && _data.offsetsMap[diningOption]) {
            offsetTime += _data.offsetsMap[diningOption].throttleTime;
        }
        return offsetTime;
    }

    function setInitialDateTime(initialDateTime) {
        data.initialMoment = moment.parseZone(initialDateTime);
    }

    function _nextAvailableMoment(diningOption) {
        let offsetTime = getOffsetTime(diningOption);
        return angular.copy(data.initialMoment).add(offsetTime, 'minutes');
    }

    /**
     * @param {moment.Moment} date
     * @param {string} diningOption
     * @returns {Promise<moment.Moment>} if time right now is before a valid service period,
     *  returns the start time of the first service period. Else null
     */
    function _beforeAServicePeriod(date, diningOption) {
        return $q.all([
            restaurantInfoFactory.getInfo(['prepTimes']),
            availabilitiesFactory.getStartEndTimes(date, diningOption)
        ]).then(function([info, startEndTimes]){
            for (var i = 0; i < startEndTimes.length; i++) {
                let timePlusPrep = availabilitiesFactory.addTimes(startEndTimes[i].startTime.clone(), diningOption, info.prepTimes);
                if (date.isBefore(startEndTimes[i].startTime) && (timePlusPrep.isBefore(startEndTimes[i].endTime)
                 || timePlusPrep.isSame(startEndTimes[i].endTime))) {
                    return startEndTimes[i].startTime;
                }
            }
            return null;
        }, function() {
            return null;
        });
    }

    /**
     * Finds the moment of the first service period after today.
     * @param {string} diningOption
     * @returns {Promise<moment.Moment>}
     */
    function _startOfNextAvailableDayMoment(diningOption) {
        // Find the next day the restaurant is open
        let deferred = $q.defer();
        let doGetStartEndTimes = i => {
            let day = data.initialMoment.clone().add(i, 'd');
            availabilitiesFactory.getStartEndTimes(day, diningOption).then(function(times) {
                firstValidServicePeriod(times, diningOption).then(function(time) {
                    if(time !== null) {
                        deferred.resolve(time);
                    } else {
                        _continueOrReject(i, doGetStartEndTimes, deferred);
                    }
                });
            }).catch(() => {
                _continueOrReject(i, doGetStartEndTimes, deferred);
            });
        };
        doGetStartEndTimes(1);
        return deferred.promise;
    }

    /**
     * Determines if the given `times` for a particular day have a "valid" service period and
     * return the start time of that service period. Note that the start time is returned
     * unmodified. "valid" meaning that it is the first service period in which there
     * would be a useable fulfillment time that the user could select. This only refers to
     * the first valid service period within the provided `times`. There may be earlier
     * valid service periods for another set of `times` for a different date.
     *
     * The first usable fulfillment time within the first valid service period
     * is not necessarily exactly at the service period's start time. Prep and
     * throttling time must be accounted for separately in order to determine that.
     *
     * @param {*} times result from `availabilitiesFactory.getStartEndTimes()`
     * @param {string} diningOption
     * @returns {Promise<moment.Moment>} resolves to the service period's start time if there is a valid one or `null` if not
     */
    function firstValidServicePeriod(times, diningOption) {
        return restaurantInfoFactory.getInfo(['prepTimes']).then(function(info) {
            // This value is with respect to the current time, not to the given
            // calendar day the service periods in `times` refer to. It is a hard-limit
            // to the overall earliest possible fire time.
            const earliestFireTime = availabilitiesFactory.addThrottlingTime(
                data.initialMoment.clone(), diningOption, info.prepTimes
            );

            for (var i = 0; i < times.length; i++) {
                // We need to clamp the firstFireTime so that we properly account for throttling.
                // i.e. firstFireTime = max(times[i].startTime, earliestFireTime)
                let firstFireTime = times[i].startTime.clone();
                const lastFulfillmentTime = times[i].endTime.clone();

                if (firstFireTime.isBefore(earliestFireTime)) {
                    firstFireTime = earliestFireTime.clone();
                }

                // First possible fulfillment time that is >= current service period's startTime,
                // but it may be > current service period's endTime as well.
                const firstFulfillmentTime = availabilitiesFactory.addPrepTime(firstFireTime.clone(), diningOption, info.prepTimes);

                // We already know that firstFulfillmentTime is >= than current service period's startTime,
                // therefore if it is also <= the endTime it must fall within the service period.
                if (firstFulfillmentTime.isSameOrBefore(lastFulfillmentTime)) {
                    return times[i].startTime;
                }
            }

            return null;
        });
    }

    function _continueOrReject(i, callback, deferred) {
        if (i < _data.scheduledOrderMaxDays) {
            callback(i + 1);
        } else {
            deferred.reject('No value from _startOfNextAvailableDayMoment()');
        }
    }

    /**
     * Gets the soonest possible fulfillment time that can be selected, searching subsequent days in
     * the schedule as necessary. Note that the returned promise will be rejected if there isn't an
     * available time, e.g. if the restaurant is closed every day.
     *
     * @param {string} diningOption
     * @returns {Promise<moment.Moment>}
     */
    function getMinMoment(diningOption) {
        return $q.all([
            availabilitiesFactory.isRestaurantOpenAtTime(diningOption, data.initialMoment.clone()),
            isRestaurantOpen(diningOption)
        ]).then(function([isOpen, isOpenWithPrep]) {
            if(isOpen && isOpenWithPrep) {
                return _nextAvailableMoment(diningOption);
            }
            return _nextOpenBusinessDaySchedule(diningOption);
        });

    }

    /**
     *
     * @param {string} diningOption
     * @returns {Promise<moment.Moment>}
     */
    function _nextOpenBusinessDaySchedule(diningOption) {
        let day = data.initialMoment.clone();
        return _beforeAServicePeriod(day, diningOption).then(function(time) {
            if (time !== null) {
                return time;
            } else {
                return _startOfNextAvailableDayMoment(diningOption);
            }
        });
    }

    function _makeMidnight(dayOfWeek) {
        let copy = angular.copy(data.initialMoment);
        copy.weekday(dayOfWeek)
            .startOf('day');
        return copy;
    }

    function _makeBusinessDayCutoff(dayOfWeek) {
        let copy = angular.copy(data.initialMoment);
        copy.weekday(dayOfWeek)
            .startOf('day')
            .hours(_data.closeoutHour);
        return copy;
    }

    function _createMoment(dayOfWeek, time) {
        let date = angular.copy(dayOfWeek);
        let timeMoment = moment.parseZone(moment(time, 'HH:mm:ss:.SSS'));
        let midnight = _makeMidnight(dayOfWeek);
        let bizCutoff = _makeBusinessDayCutoff(dayOfWeek);
        date.set({
            hour: timeMoment.hours(),
            minute: timeMoment.minutes(),
            second: timeMoment.seconds(),
            millisecond: timeMoment.milliseconds()
        });

        // If this day is between midnight and the business day cutoff (default: 4am)
        // this day is actually the NEXT day. because our day ends at the cutoff
        if(date.isBetween(midnight, bizCutoff)) {
            date.add(1, 'day');
        }

        return date;
    }

    function _isOpenforDay(diningOption, dayOfWeek) {
        return restaurantInfoFactory.getInfo(['prepTimes']).then(info => {
            let dayOfWeekPlusPrep = availabilitiesFactory.addTimes(dayOfWeek.clone(), diningOption, info.prepTimes);
            return availabilitiesFactory.isRestaurantOpenAtTime(diningOption, dayOfWeekPlusPrep);
        });
    }

    function _timeUntilCloseForDay(diningOption, dayOfWeek) {
        return availabilitiesFactory.formatOOSchedule(diningOption).then(function(schedules) {
            let today = dayOfWeek.isoWeekday() - 1;
            let todaySchedule = schedules[today];
            let initialMoment = angular.copy(data.initialMoment);
            return Math.abs(_createMoment(initialMoment, todaySchedule[todaySchedule.length - 1].endTime).diff(initialMoment));
        });
    }

    function isRestaurantOpen(diningOption) {
        let today = angular.copy(data.initialMoment);
        return _isOpenforDay(diningOption, today);
    }

    function timeUntilClose(diningOption) {
        let today = angular.copy(data.initialMoment);
        let tomorrow = moment(today).add(1, 'days');
        return $q.all([
            _isOpenforDay(diningOption, today),
            _isOpenforDay(diningOption, tomorrow)
        ]).then(function([openToday, openTomorrow]) {
            if(openToday) {
                return _timeUntilCloseForDay(diningOption, today);
            } else if(openTomorrow) {
                return _timeUntilCloseForDay(diningOption, tomorrow);
            } else {
                return null;
            }

        });
    }

    // Testing shim for availabilitiesFactory tests
    function getRestaurantAvailability(diningOption) {
        if(diningOption === 'PICKUP') {
            diningOption = ORDER_TYPES.TAKE_OUT;
        }
        return initialize().then(function() {
            return isRestaurantOpen(diningOption);
        });
    }

    function _getWeekday() {
        let date = angular.copy(data.initialMoment);
        const yesterday = angular.copy(date).subtract(1, 'day');
        let midnight = _makeMidnight(date.weekday());
        let bizCutoff = _makeBusinessDayCutoff(date.weekday());

        if(data.initialMoment.isBetween(midnight, bizCutoff)) {
            return yesterday.weekday();
        }

        return date.weekday();
    }

    function isSameDay(dateTime) {
        let requestedDay = data.initialMoment;
        if (dateTime !== null) {
            requestedDay = moment.parseZone(dateTime);
        }
        const todayDay = _getWeekday();
        const tomorrowDay = todayDay + 1;
        const today = _makeBusinessDayCutoff(todayDay);
        const tomorrow = _makeBusinessDayCutoff(tomorrowDay);
        return requestedDay.isBetween(today, tomorrow, '[)');
    }
}
